import { type MotionValue } from 'motion/react';

import {
  BIRD_HITBOX_INSET,
  BIRD_ROTATION_MAX,
  BIRD_ROTATION_MIN,
  BIRD_SIZE,
  BIRD_X_RATIO,
  FLAP_VELOCITY,
  GRAVITY,
  MAX_FALL_SPEED,
  FIRST_PIPE_LEAD,
  MAX_FRAME_SECONDS,
  MAX_PIPE_COUNT,
  MIN_PIPE_COUNT,
  PIPE_GAP,
  PIPE_MARGIN,
  PIPE_OVERSHOOT,
  PIPE_SPACING,
  PIPE_SPEED,
  PIPE_START_GAP,
  PIPE_WIDTH,
  PLAY_BOTTOM_INSET,
  PLAY_TOP_INSET,
} from './bird-game.constants';

export type Pipe = { x: number; gapY: number; scored: boolean };

export type BirdGameState = {
  birdY: number;
  birdVelocity: number;
  birdX: number;
  pipes: Pipe[];
  playWidth: number;
  topBound: number;
  bottomBound: number;
  score: number;
};

export type StepOutcome = { scored: boolean; crashed: boolean };

export type PipeMotion = {
  x: MotionValue<number>;
  topHeight: MotionValue<number>;
  bottomTop: MotionValue<number>;
  bottomHeight: MotionValue<number>;
};

export type BirdGameMotion = {
  birdX: MotionValue<number>;
  birdY: MotionValue<number>;
  birdRotation: MotionValue<number>;
  pipes: PipeMotion[];
};

type EngineCallbacks = {
  onScore: (score: number) => void;
  onGameOver: (score: number) => void;
};

export const playTopBound = () => PLAY_TOP_INSET;
export const playBottomBound = (height: number) => height - PLAY_BOTTOM_INSET;

export const createBirdGameState = (): BirdGameState => ({
  birdY: 0,
  birdVelocity: 0,
  birdX: 0,
  pipes: [],
  playWidth: 0,
  topBound: 0,
  bottomBound: 0,
  score: 0,
});

export const hasPlayableBounds = (state: BirdGameState) =>
  state.playWidth > 0 && state.bottomBound - state.topBound > BIRD_SIZE;

const gapRange = (state: BirdGameState) => ({
  min: state.topBound + PIPE_GAP / 2 + PIPE_MARGIN,
  max: state.bottomBound - PIPE_GAP / 2 - PIPE_MARGIN,
});

export function randomGapY(state: BirdGameState): number {
  const { min, max } = gapRange(state);
  if (max <= min) return (state.topBound + state.bottomBound) / 2;
  return min + Math.random() * (max - min);
}

export function pipeCountFor(width: number): number {
  const spanning = Math.ceil((width + PIPE_WIDTH) / PIPE_SPACING);
  return Math.min(MAX_PIPE_COUNT, Math.max(MIN_PIPE_COUNT, spanning));
}

export function layOutPipes(state: BirdGameState): void {
  const firstX = Math.min(
    state.playWidth + PIPE_START_GAP,
    state.birdX + FIRST_PIPE_LEAD
  );
  state.pipes.forEach((pipe, index) => {
    pipe.x = firstX + index * PIPE_SPACING;
  });
}

export function seedPipes(state: BirdGameState): void {
  state.pipes = Array.from({ length: pipeCountFor(state.playWidth) }, () => ({
    x: 0,
    gapY: randomGapY(state),
    scored: false,
  }));
  layOutPipes(state);
}

export function birdRotationFor(velocity: number): number {
  const progress =
    (velocity - FLAP_VELOCITY) / (MAX_FALL_SPEED - FLAP_VELOCITY);
  const clamped = Math.min(1, Math.max(0, progress));
  return BIRD_ROTATION_MIN + clamped * (BIRD_ROTATION_MAX - BIRD_ROTATION_MIN);
}

export function refitToBounds(state: BirdGameState): void {
  state.birdY = Math.min(
    state.bottomBound - BIRD_SIZE,
    Math.max(state.topBound, state.birdY)
  );
  state.birdX = state.playWidth * BIRD_X_RATIO;
  const { min, max } = gapRange(state);
  const fallback = (state.topBound + state.bottomBound) / 2;
  for (const pipe of state.pipes) {
    pipe.gapY = max <= min ? fallback : Math.min(max, Math.max(min, pipe.gapY));
  }
}

export function stepBirdGame(
  state: BirdGameState,
  deltaSeconds: number
): StepOutcome {
  const dt = Math.min(deltaSeconds, MAX_FRAME_SECONDS);

  state.birdVelocity = Math.min(
    state.birdVelocity + GRAVITY * dt,
    MAX_FALL_SPEED
  );
  state.birdY += state.birdVelocity * dt;

  if (
    state.birdY < state.topBound ||
    state.birdY + BIRD_SIZE > state.bottomBound
  ) {
    return { scored: false, crashed: true };
  }

  const halfWidth = BIRD_SIZE / 2 - BIRD_HITBOX_INSET;
  const birdLeft = state.birdX - halfWidth;
  const birdRight = state.birdX + halfWidth;
  const birdTop = state.birdY + BIRD_HITBOX_INSET;
  const birdBottom = state.birdY + BIRD_SIZE - BIRD_HITBOX_INSET;

  let rightmost = -Infinity;
  for (const pipe of state.pipes) {
    pipe.x -= PIPE_SPEED * dt;
    if (pipe.x > rightmost) rightmost = pipe.x;
  }

  let scored = false;
  let crashed = false;
  for (const pipe of state.pipes) {
    if (pipe.x + PIPE_WIDTH < 0) {
      rightmost += PIPE_SPACING;
      pipe.x = rightmost;
      pipe.gapY = randomGapY(state);
      pipe.scored = false;
    }
    if (!pipe.scored && pipe.x + PIPE_WIDTH < state.birdX) {
      pipe.scored = true;
      scored = true;
    }
    const overlapsHorizontally =
      birdRight > pipe.x && birdLeft < pipe.x + PIPE_WIDTH;
    const outsideGap =
      birdTop < pipe.gapY - PIPE_GAP / 2 ||
      birdBottom > pipe.gapY + PIPE_GAP / 2;
    if (overlapsHorizontally && outsideGap) crashed = true;
  }

  if (scored) state.score += 1;
  return { scored, crashed };
}

export type BirdGameEngine = ReturnType<typeof createBirdGameEngine>;

export function createBirdGameEngine(
  motion: BirdGameMotion,
  { onScore, onGameOver }: EngineCallbacks
) {
  const state = createBirdGameState();
  let frameHandle: number | null = null;
  let lastFrameAt = 0;
  let runState: 'parked' | 'running' | 'paused' | 'crashed' = 'parked';

  const centerBird = () => {
    state.birdVelocity = 0;
    state.birdY = (state.topBound + state.bottomBound) / 2 - BIRD_SIZE / 2;
  };

  const parkCourse = () => {
    if (!hasPlayableBounds(state)) state.pipes = [];
    else if (state.pipes.length === pipeCountFor(state.playWidth)) {
      layOutPipes(state);
    } else seedPipes(state);
  };

  const syncPipes = () => {
    motion.pipes.forEach((channel, index) => {
      const pipe = state.pipes[index];
      if (!pipe) {
        channel.topHeight.set(0);
        channel.bottomHeight.set(0);
        return;
      }
      const gapTop = pipe.gapY - PIPE_GAP / 2;
      const gapBottom = pipe.gapY + PIPE_GAP / 2;
      const stageHeight = state.bottomBound + PLAY_BOTTOM_INSET;
      channel.x.set(pipe.x);
      channel.topHeight.set(Math.max(gapTop + PIPE_OVERSHOOT, 0));
      channel.bottomTop.set(gapBottom);
      channel.bottomHeight.set(
        Math.max(stageHeight + PIPE_OVERSHOOT - gapBottom, 0)
      );
    });
  };

  const syncBird = () => {
    motion.birdX.set(state.birdX);
    motion.birdY.set(state.birdY);
    motion.birdRotation.set(
      runState === 'parked' ? 0 : birdRotationFor(state.birdVelocity)
    );
  };

  const cancelFrame = () => {
    if (frameHandle === null) return;
    cancelAnimationFrame(frameHandle);
    frameHandle = null;
  };

  const onFrame = (now: number) => {
    const outcome = stepBirdGame(state, (now - lastFrameAt) / 1000);
    lastFrameAt = now;
    syncBird();
    syncPipes();

    if (outcome.scored) onScore(state.score);
    if (outcome.crashed) {
      runState = 'crashed';
      cancelFrame();
      motion.birdRotation.set(BIRD_ROTATION_MAX);
      onGameOver(state.score);
      return;
    }
    frameHandle = requestAnimationFrame(onFrame);
  };

  const scheduleFrames = () => {
    lastFrameAt = performance.now();
    frameHandle = requestAnimationFrame(onFrame);
  };

  return {
    setBounds(width: number, height: number) {
      state.playWidth = width;
      state.topBound = playTopBound();
      state.bottomBound = playBottomBound(height);
      if (runState === 'parked') centerBird();
      refitToBounds(state);
      if (runState === 'parked') parkCourse();
      syncBird();
      syncPipes();
    },

    resetToSpawn() {
      cancelFrame();
      runState = 'parked';
      centerBird();
      parkCourse();
      state.score = 0;
      syncBird();
      syncPipes();
    },

    startRun(): boolean {
      if (!hasPlayableBounds(state)) return false;
      cancelFrame();
      centerBird();
      if (runState !== 'parked') seedPipes(state);
      runState = 'running';
      state.score = 0;
      state.birdVelocity = FLAP_VELOCITY;
      syncBird();
      syncPipes();
      scheduleFrames();
      return true;
    },

    flap() {
      state.birdVelocity = FLAP_VELOCITY;
    },

    pause() {
      if (runState !== 'running') return;
      cancelFrame();
      runState = 'paused';
    },

    resume() {
      if (runState !== 'paused') return;
      runState = 'running';
      scheduleFrames();
    },

    stop() {
      cancelFrame();
      runState = 'parked';
    },
  };
}
